// Global Instructions Rule Applied!
import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../state_manager/hooks/useAppDispatch'
import { useDrawer } from '../../../core/contexts/DrawerContext.jsx'
import { toggleTaskCompleted, updateTask } from '../../../store/slices/tasksSlice'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { TaskRecordFields } from '../../../state_manager/data_models/task_record.ts'
import { selectActiveRecordById } from '../../../state_manager/state_update_actions/active_records/selectors.ts'
import { selectDealById } from '../../../state_manager/state_update_actions/deals/selectors.ts'
import {
  setActiveRecord,
  registerRecordUsage
} from '../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { fetchDealById } from '../services/dealsService.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
// Optimistic updates removed - no longer supported

/**
 * Custom hook for managing drawer state and operations for deals
 * Handles drawer opening, closing, and form submission logic
 */
export const useDealsDrawer = (deals: any[]) => {
  const {
    isOpen: isDrawerOpen,
    recordId,
    isNew,
    openDrawerForNew,
    openDrawerForRecord,
    closeDrawer,
    formType
  } = useDrawer()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const dealsState = useSelector((state: any) => state.deals)

  // Drawer-specific state
  const [selectedDeal, setSelectedDeal] = useState<any>(null)
  const hasAttemptedFetch = useRef<boolean>(false)

  // Drawer handlers
  const handleOpenDrawerForNew = useCallback(() => {
    setSelectedDeal(null)
    openDrawerForNew()
  }, [openDrawerForNew])

  const handleOpenDrawerForDeal = useCallback(
    (dealId: string) => {
      const deal = deals.find((d) => d[DealFields.deals__id] === dealId || d.deals__id === dealId)
      setSelectedDeal(deal)
      openDrawerForRecord(dealId)
    },
    [deals, openDrawerForRecord]
  )

  const handleDealNameClick = useCallback(
    (fieldKey: string, value: any, dealId: string) => {
      if (dealId) {
        handleOpenDrawerForDeal(dealId)
      }
    },
    [handleOpenDrawerForDeal]
  )

  // Handle tasks click to open drawer with tasks focus
  const handleTasksClick = useCallback(
    (tasks: any[], dealRecord: any, taskId: string | null = null) => {
      // Extract deal ID from the deal record passed from the table
      const dealId = dealRecord?.id

      if (dealId) {
        // Build query parameters
        const params = new URLSearchParams()

        // If specific task ID is provided, add it to open that task's detail panel
        if (taskId) {
          params.set('taskId', taskId)
        }

        // Open drawer with or without query parameters
        const queryString = params.toString()
        navigate(`/deals/forms/drawer/${dealId}${queryString ? `?${queryString}` : ''}`)
      }
    },
    [navigate]
  )

  // Handle task completion toggle
  const handleTaskToggle = useCallback(
    async (taskId: string, dealId: string, completed: boolean) => {
      try {
        // Update tasks slice immediately for instant feedback
        dispatch(toggleTaskCompleted({ taskId, dealId }))

        // Update backend - optimistic updates removed
        await dispatch(
          updateTask({
            dealId,
            taskId,
            taskData: {
              [TaskRecordFields.tasks__id]: taskId,
              [TaskRecordFields.tasks__completed]: completed
            }
          })
        ).unwrap()
      } catch (error) {
        // If backend update fails, revert task slice
        dispatch(toggleTaskCompleted({ taskId, dealId }))
        console.error('Failed to update task:', error)
      }
    },
    [dispatch]
  )

  // handleDrawerSubmit removed - submission is now handled in MainTabContainer.jsx

  const handleDrawerCancel = useCallback(() => {
    setSelectedDeal(null)
    closeDrawer()
  }, [closeDrawer])

  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [recordId])

  // Get deal from activeRecords first
  const dealFromActiveRecords = useSelector((state: any) =>
    recordId && !isNew ? selectActiveRecordById(state, recordId) : null
  )

  // Find deal when recordId changes (for editing existing deals)
  useEffect(() => {
    // Only proceed if we're actually on the deals route
    // Check both formType from drawer context and location pathname
    const isDealsRoute = formType === 'deal' || location.pathname.startsWith('/deals')

    if (!isDealsRoute) {
      // Not on deals route, don't fetch
      return
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (recordId && !isNew && currentToken) {
      // First check activeRecords
      if (dealFromActiveRecords) {
        setSelectedDeal(dealFromActiveRecords)
        hasAttemptedFetch.current = true
        return
      }

      // Then check deals array passed as prop
      const deal = deals.find(
        (d) => d[DealFields.deals__id] === recordId || d.deals__id === recordId || d.id === recordId
      )

      if (deal) {
        setSelectedDeal(deal)
        hasAttemptedFetch.current = true
      } else if (!hasAttemptedFetch.current) {
        // Check Redux state using selector
        const dealFromState = selectDealById({ deals: dealsState }, recordId)

        if (dealFromState) {
          setSelectedDeal(dealFromState)
          hasAttemptedFetch.current = true
        } else {
          // Fetch from backend if not found anywhere
          hasAttemptedFetch.current = true
          fetchDealById(recordId, currentToken)
            .then((response: any) => {
              if (response.data && !response.error) {
                const fetchedDeal = response.data
                // Only add to activeRecords, not module state
                const dealIdString = recordId.toString()
                dispatch(setActiveRecord({ uuid: dealIdString, record: fetchedDeal }))
                dispatch(registerRecordUsage({ uuid: dealIdString, context: 'drawer' }))
                setSelectedDeal(fetchedDeal)
              } else if (response.error) {
                console.error('Error fetching deal:', response.error)
                setSelectedDeal(null)
              }
            })
            .catch((error: any) => {
              console.error('Error fetching deal:', error)
              setSelectedDeal(null)
            })
        }
      }
    } else if (isNew || !recordId) {
      setSelectedDeal(null)
      hasAttemptedFetch.current = false
    }
  }, [recordId, isNew, deals, dispatch, dealsState, dealFromActiveRecords, formType, location.pathname])

  return {
    // Drawer state
    isDrawerOpen,
    recordId,
    isNew,
    selectedDeal,

    // Handlers
    handleOpenDrawerForNew,
    handleOpenDrawerForDeal,
    handleDealNameClick,
    handleTasksClick,
    handleTaskToggle,
    handleDrawerCancel
  }
}
